/**
 * Bundle Section JavaScript
 * Handles bundle product selection, variant changes, price calculation, and cart addition
 */

(function() {
  'use strict';

  /**
   * Format money value using Shopify's money format
   * Removes ".00" cents, wraps non-zero cents in <sup> tag
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string with HTML
   */
  function formatMoney(cents) {
    // Get currency symbol from the page
    const currencyEl = document.querySelector('[data-currency]');
    const currencySymbol = currencyEl?.dataset.currency || '$';
    
    // Format as currency
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencySymbol === '$' ? 'USD' : currencySymbol,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
    
    // Check if cents are "00"
    const centsValue = cents % 100;
    
    if (centsValue === 0) {
      // Remove ".00" from the formatted string
      return formatted.replace(/\.00$/, '');
    } else {
      // Wrap cents in <sup> tag with period before it
      // Extract the decimal part (e.g., ".50" from "$100.50")
      const parts = formatted.split('.');
      if (parts.length === 2) {
        const dollars = parts[0];
        const centsStr = parts[1];
        return `${dollars}<sup>.${centsStr}</sup>`;
      }
      return formatted;
    }
  }

  /**
   * Update bundle price summary
   * @param {string} sectionId - Section ID
   */
  function updateBundlePrice(sectionId) {
    const container = document.querySelector(`#boost-products-container-${sectionId}`);
    if (!container) return;

    let totalPrice = 0;
    let totalComparePrice = 0;
    let selectedCount = 0;
    let maxDiscountPct = 0;

    // Find the active/visible bundle grid (either collection or global)
    // Collection grids are hidden by default, global is shown by default
    let activeGrid = container.querySelector('.bundle-products-grid.shop-the-look-global:not(.bundle-product-hidden)');
    
    // Check if a collection grid is visible (it would have been shown by JS)
    if (!activeGrid || activeGrid.classList.contains('bundle-product-hidden') || window.getComputedStyle(activeGrid).display === 'none') {
      // Try to find a visible collection grid
      const collectionGrids = container.querySelectorAll('.bundle-products-grid.shop-the-look-collection');
      for (const grid of collectionGrids) {
        if (!grid.classList.contains('bundle-product-hidden') && window.getComputedStyle(grid).display !== 'none') {
          activeGrid = grid;
          break;
        }
      }
    }
    
    // If still no active grid, fall back to global
    if (!activeGrid || activeGrid.classList.contains('bundle-product-hidden') || window.getComputedStyle(activeGrid).display === 'none') {
      activeGrid = container.querySelector('.bundle-products-grid.shop-the-look-global');
    }
    
    if (!activeGrid) return;

    // Get product wrappers from the active grid only
    const productWrappers = activeGrid.querySelectorAll('.bundle-product-wrapper[data-original-product-id]');
    
    // First, check if all 3 products are checked
    let checkedCount = 0;
    const firstThreeWrappers = Array.from(productWrappers).slice(0, 3);
    firstThreeWrappers.forEach((wrapper) => {
      const card = wrapper.querySelector('.bundle-product-card');
      if (card) {
        const checkbox = card.querySelector('.bundle-product-card__checkbox');
        if (checkbox && checkbox.checked) {
          checkedCount++;
        }
      }
    });
    
    // Discount only applies if ALL 3 products are checked
    const allThreeChecked = checkedCount === 3;
    
    // Only calculate totals from first 3 products in the active bundle (from metafield)
    // Use the displayed sale price from each card (bundle-product-card__price--sale) as the source of truth
    productWrappers.forEach((wrapper) => {
      // Stop if we already have 3 items (bundle is limited to 3 products)
      if (selectedCount >= 3) return;
      
      const card = wrapper.querySelector('.bundle-product-card');
      if (!card) return;
      
      const checkbox = card.querySelector('.bundle-product-card__checkbox');
      if (!checkbox || !checkbox.checked) return;

      const dropdown = card.querySelector('.bundle-product-card__variant-dropdown');
      if (!dropdown) return;

      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (!selectedOption || selectedOption.disabled) return;

      // Primary: Get price from the displayed sale price element (source of truth - matches what user sees)
      const salePriceEl = card.querySelector('.bundle-product-card__price--sale');
      let itemPrice = 0;
      let itemComparePrice = 0;

      if (salePriceEl && salePriceEl.dataset.price) {
        itemPrice = parseInt(salePriceEl.dataset.price, 10) || 0;
        const comparePriceEl = card.querySelector('.bundle-product-card__price--compare');
        if (comparePriceEl && comparePriceEl.dataset.comparePrice) {
          const compareVal = parseInt(comparePriceEl.dataset.comparePrice, 10) || 0;
          if (compareVal > itemPrice && compareVal > 0) {
            itemComparePrice = compareVal;
          }
        }
        const discountPct = parseInt(wrapper.dataset.bundleDiscountPct || '0', 10);
        if (discountPct > maxDiscountPct) {
          maxDiscountPct = discountPct;
        }
      }

      // Fallback: Calculate from dropdown option data if sale price element is missing or invalid
      if (itemPrice <= 0) {
        let originalPrice = parseInt(selectedOption.dataset.compareAtPrice || '0', 10);
        if (originalPrice === 0) {
          originalPrice = parseInt(selectedOption.dataset.price || '0', 10);
        }
        const discountPct = parseInt(wrapper.dataset.bundleDiscountPct || '0', 10);
        
        if (allThreeChecked && discountPct > 0 && originalPrice > 0) {
          itemComparePrice = originalPrice;
          const discountMultiplier = (100 - discountPct) / 100;
          itemPrice = Math.round(originalPrice * discountMultiplier);
          if (discountPct > maxDiscountPct) {
            maxDiscountPct = discountPct;
          }
        } else {
          itemPrice = originalPrice;
          const shopifyComparePrice = parseInt(selectedOption.dataset.compareAtPrice || '0', 10);
          if (shopifyComparePrice > 0 && shopifyComparePrice > originalPrice) {
            itemComparePrice = shopifyComparePrice;
          }
        }
      }

      totalPrice += itemPrice;
      if (itemComparePrice > 0) {
        totalComparePrice += itemComparePrice;
      }
      selectedCount++;
    });

    // Update price display
    const totalPriceEl = document.querySelector(`#bundle-total-price-${sectionId}`);
    const originalPriceEl = document.querySelector(`#bundle-original-price-${sectionId}`);
    const productCountEl = document.querySelector(`#bundle-product-count-${sectionId}`);
    const discountBadgeEl = document.querySelector(`#bundle-discount-badge-${sectionId}`);
    const discountPercentageEl = document.querySelector(`#bundle-discount-percentage-${sectionId}`);

    // Check if any discount is applied (maxDiscountPct > 0 means at least one checked item has discount)
    const hasDiscount = maxDiscountPct > 0;

    if (totalPriceEl) {
      totalPriceEl.innerHTML = formatMoney(totalPrice);
      // Apply regular price styling if no discount
      if (hasDiscount) {
        totalPriceEl.classList.remove('bundle-price-regular');
        totalPriceEl.style.color = ''; // Reset to default (red from CSS)
      } else {
        totalPriceEl.classList.add('bundle-price-regular');
        totalPriceEl.style.color = '#231f20'; // Black color for regular price
      }
    }

    if (originalPriceEl) {
      if (hasDiscount && totalComparePrice > totalPrice && totalComparePrice > 0) {
        originalPriceEl.innerHTML = formatMoney(totalComparePrice);
        originalPriceEl.classList.remove('bundle-price-hidden');
        originalPriceEl.style.textDecoration = 'line-through';
      } else {
        originalPriceEl.classList.add('bundle-price-hidden');
      }
    }

    if (productCountEl) {
      productCountEl.textContent = selectedCount;
    }

    // Update discount badge
    if (discountBadgeEl && discountPercentageEl) {
      if (hasDiscount) {
        discountPercentageEl.textContent = maxDiscountPct;
        discountBadgeEl.classList.remove('bundle-price-hidden');
      } else {
        discountBadgeEl.classList.add('bundle-price-hidden');
      }
    }

    // Update add button state
    const addButton = document.querySelector(`#bundle-add-all-button-${sectionId}`);
    if (addButton) {
      if (selectedCount === 0) {
        addButton.disabled = true;
        addButton.classList.add('disabled');
      } else {
        addButton.disabled = false;
        addButton.classList.remove('disabled');
      }
    }
  }

  /**
   * Update product card images when variant changes
   * @param {HTMLElement} card - Product card element
   * @param {HTMLOptionElement} selectedOption - Selected option element
   */
  function updateProductCardImages(card, selectedOption) {
    let primaryImageUrl = selectedOption.dataset.imageUrl || '';
    let secondaryImageUrl = selectedOption.dataset.secondaryImageUrl || '';
    
    // Prefer "next image in product ordering" after the selected variant's primary image
    // This matches the logic in sunice-product-card.js
    try {
      const raw = card.dataset.productImages;
      if (raw) {
        /** @type {string[]} */
        const ordered = JSON.parse(raw);
        const normalize = (url) => {
          if (!url) return '';
          const withoutHost = url.replace(/^https?:\/\/[^/]+/i, '');
          return withoutHost.split('?')[0].split('#')[0];
        };
        const primaryKey = normalize(primaryImageUrl);
        const idx = ordered.findIndex((u) => normalize(u) === primaryKey);
        if (idx >= 0 && ordered.length > 1) {
          // Find the next DIFFERENT image after the primary (skip duplicates)
          for (let i = idx + 1; i < ordered.length; i++) {
            const candidate = ordered[i];
            const candKey = normalize(candidate);
            // Also avoid picking another variant's primary "a" image as hover.
            // We prefer the immediate "b" follow-up image if your naming follows *_a / *_b.
            if (candKey && candKey !== primaryKey) {
              const looksLikeAnotherPrimary =
                /_a(\.|$)/i.test(candKey) && !/_a(\.|$)/i.test(primaryKey);
              if (looksLikeAnotherPrimary) continue;
              secondaryImageUrl = candidate || secondaryImageUrl;
              break;
            }
          }
        }
      }
    } catch (e) {
      // ignore JSON/parse errors and fall back to data-secondary-image-url
    }
    
    // Use existing function if available, otherwise update directly
    if (typeof window.updateProductCardPrimaryImage === 'function') {
      window.updateProductCardPrimaryImage(card, primaryImageUrl, secondaryImageUrl);
    } else {
      // Fallback: update images directly
      const primaryImage = card.querySelector('.product-image-primary');
      const secondaryImage = card.querySelector('.product-image-secondary');
      
      if (primaryImage instanceof HTMLImageElement && primaryImageUrl) {
        if (!primaryImage.dataset.originalSrc) {
          primaryImage.dataset.originalSrc = primaryImage.currentSrc || primaryImage.src;
        }
        primaryImage.src = primaryImageUrl;
        primaryImage.srcset = primaryImageUrl;
        primaryImage.dataset.variantId = selectedOption.value;
      }
      
      if (secondaryImage instanceof HTMLImageElement && secondaryImageUrl) {
        if (!secondaryImage.dataset.originalSrc) {
          secondaryImage.dataset.originalSrc = secondaryImage.currentSrc || secondaryImage.src;
        }
        secondaryImage.src = secondaryImageUrl;
        secondaryImage.srcset = secondaryImageUrl;
        secondaryImage.dataset.variantId = selectedOption.value;
      } else if (secondaryImageUrl && !secondaryImage) {
        // Create secondary image if it doesn't exist but we have a URL
        const imageContainer = card.querySelector('.product-card__image, .bundle-product-card__image');
        if (imageContainer) {
          const newSecondaryImage = document.createElement('img');
          newSecondaryImage.src = secondaryImageUrl;
          newSecondaryImage.srcset = secondaryImageUrl;
          newSecondaryImage.alt = primaryImage?.alt || '';
          newSecondaryImage.loading = 'lazy';
          newSecondaryImage.width = 300;
          newSecondaryImage.height = 300;
          newSecondaryImage.className = 'product-image-secondary';
          newSecondaryImage.dataset.variantId = selectedOption.value;
          imageContainer.appendChild(newSecondaryImage);
        }
      }
    }
  }

  /**
   * Update individual product card price and sale badge
   * @param {HTMLElement} card - Product card element
   */
  function updateProductCardPrice(card) {
    const dropdown = card.querySelector('.bundle-product-card__variant-dropdown');
    if (!dropdown) return;

    const selectedOption = dropdown.options[dropdown.selectedIndex];
    if (!selectedOption) return;

    // Check if checkbox is checked
    const checkbox = card.querySelector('.bundle-product-card__checkbox');
    const isChecked = checkbox ? checkbox.checked : true;

    // Get discount percentage from wrapper
    const wrapper = card.closest('.bundle-product-wrapper');
    const discountPct = wrapper ? parseInt(wrapper.dataset.bundleDiscountPct || '0', 10) : 0;
    
    // Check if all 3 products are checked (discount only applies when all 3 are checked)
    const container = card.closest('.boost-products-container');
    let allThreeChecked = false;
    if (container) {
      const activeGrid = container.querySelector('.bundle-products-grid:not([style*="display: none"])') || 
                        container.querySelector('.bundle-products-grid');
      if (activeGrid) {
        const productWrappers = activeGrid.querySelectorAll('.bundle-product-wrapper[data-original-product-id]');
        const firstThreeWrappers = Array.from(productWrappers).slice(0, 3);
        let checkedCount = 0;
        firstThreeWrappers.forEach((w) => {
          const c = w.querySelector('.bundle-product-card');
          if (c) {
            const cb = c.querySelector('.bundle-product-card__checkbox');
            if (cb && cb.checked) {
              checkedCount++;
            }
          }
        });
        allThreeChecked = checkedCount === 3;
      }
    }

    // Get original price from option (if compare-at-price is set, use that as original, otherwise use price)
    let originalPrice = parseInt(selectedOption.dataset.compareAtPrice || '0', 10);
    if (originalPrice === 0) {
      // If no compare price, the price itself is the original (no discount applied yet)
      originalPrice = parseInt(selectedOption.dataset.price || '0', 10);
    }

    // Calculate prices with faux discount (only if checkbox is checked AND all 3 products are checked)
    let price = originalPrice;
    let comparePrice = 0;

    if (isChecked && allThreeChecked && discountPct > 0 && originalPrice > 0) {
      // Apply faux discount: compare price = original, sale price = discounted
      // Only applies when ALL 3 products are checked
      comparePrice = originalPrice;
      const discountMultiplier = (100 - discountPct) / 100;
      price = Math.round(originalPrice * discountMultiplier);
    } else {
      // No discount (either unchecked or no discount metafield) - show original price
      // Check if option already has compare price (from Shopify compare_at_price)
      comparePrice = parseInt(selectedOption.dataset.compareAtPrice || '0', 10);
      price = originalPrice; // Always use original price when unchecked
    }

    const priceEl = card.querySelector('.bundle-product-card__price--sale');
    const comparePriceEl = card.querySelector('.bundle-product-card__price--compare');
    const saleBadge = card.querySelector('.product-card.sale-badge');
    const imageContainer = card.querySelector('.product-card__image, .bundle-product-card__image');
    const variantTitleEl = card.querySelector('.bundle-product-card__brand[data-variant-title]');

    // Update variant title (brand element)
    if (variantTitleEl && selectedOption.dataset.variantTitle) {
      // Remove " - Sold Out" suffix if present
      const variantTitle = selectedOption.dataset.variantTitle.trim();
      variantTitleEl.textContent = variantTitle;
    }

    // Update images when variant changes
    updateProductCardImages(card, selectedOption);

    // Update price display
    if (priceEl) {
      priceEl.innerHTML = formatMoney(price);
      priceEl.dataset.price = price;
    }

    // Update compare price
    // Show compare whenever provided (>0). Bundle cards may use compare as "original price"
    // and price as discounted price.
    if (comparePrice > 0 && comparePrice > price) {
      if (comparePriceEl) {
        comparePriceEl.innerHTML = formatMoney(comparePrice);
        comparePriceEl.dataset.comparePrice = comparePrice;
        comparePriceEl.classList.remove('bundle-price-hidden');
        comparePriceEl.style.textDecoration = 'line-through';
      } else {
        const pricesWrap = card.querySelector('.bundle-product-card__prices');
        if (pricesWrap) {
          const newCompare = document.createElement('span');
          newCompare.className = 'bundle-product-card__price bundle-product-card__price--compare';
          newCompare.dataset.comparePrice = comparePrice;
          newCompare.innerHTML = formatMoney(comparePrice);
          newCompare.style.textDecoration = 'line-through';
          pricesWrap.appendChild(newCompare);
        }
      }
    } else if (comparePriceEl) {
      comparePriceEl.classList.add('bundle-price-hidden');
    }

    // Update sale badge with discount percentage (only if checked, all 3 checked, and discount exists)
    if (isChecked && allThreeChecked && discountPct > 0) {
      // Use the discount percentage from metafield
      if (saleBadge) {
        saleBadge.textContent = `${discountPct}% off`;
        saleBadge.classList.remove('bundle-price-hidden');
      } else if (imageContainer) {
        // Create sale badge if it doesn't exist
        const badge = document.createElement('div');
        badge.className = 'product-card sale-badge';
        badge.textContent = `${discountPct}% off`;
        imageContainer.appendChild(badge);
      }
    } else if (isChecked && comparePrice > price && comparePrice > 0) {
      // Fallback to calculated percentage if no metafield discount
      const priceDiff = comparePrice - price;
      const salePercentage = Math.round((priceDiff / comparePrice) * 100);
      
      if (salePercentage > 0) {
        if (saleBadge) {
          saleBadge.textContent = `${salePercentage}% off`;
          saleBadge.classList.remove('bundle-price-hidden');
        } else if (imageContainer) {
          // Create sale badge if it doesn't exist
          const badge = document.createElement('div');
          badge.className = 'product-card sale-badge';
          badge.textContent = `${salePercentage}% off`;
          imageContainer.appendChild(badge);
        }
      } else if (saleBadge) {
        saleBadge.classList.add('bundle-price-hidden');
      }
    } else if (saleBadge) {
      saleBadge.classList.add('bundle-price-hidden');
    }
  }

  /**
   * Collect selected products for cart
   * @param {string} sectionId - Section ID
   * @returns {Array} Array of cart items
   */
  function collectBundleItems(sectionId) {
    const container = document.querySelector(`#boost-products-container-${sectionId}`);
    if (!container) return [];

    const items = [];
    
    // Find the active/visible bundle grid (either collection or global)
    // Collection grids are hidden by default, global is shown by default
    let activeGrid = container.querySelector('.bundle-products-grid.shop-the-look-global:not(.bundle-product-hidden)');
    
    // Check if a collection grid is visible (it would have been shown by JS)
    if (!activeGrid || activeGrid.classList.contains('bundle-product-hidden') || window.getComputedStyle(activeGrid).display === 'none') {
      // Try to find a visible collection grid
      const collectionGrids = container.querySelectorAll('.bundle-products-grid.shop-the-look-collection');
      for (const grid of collectionGrids) {
        if (!grid.classList.contains('bundle-product-hidden') && window.getComputedStyle(grid).display !== 'none') {
          activeGrid = grid;
          break;
        }
      }
    }
    
    // If still no active grid, fall back to global
    if (!activeGrid || activeGrid.classList.contains('bundle-product-hidden') || window.getComputedStyle(activeGrid).display === 'none') {
      activeGrid = container.querySelector('.bundle-products-grid.shop-the-look-global');
    }
    
    if (!activeGrid) return [];

    // Get product wrappers from the active grid only
    const productWrappers = activeGrid.querySelectorAll('.bundle-product-wrapper[data-original-product-id]');
    
    // Only collect first 3 products from the active bundle metafield
    productWrappers.forEach((wrapper, index) => {
      // Stop if we already have 3 items (bundle is limited to 3 products)
      if (items.length >= 3) return;
      
      const card = wrapper.querySelector('.bundle-product-card');
      if (!card) return;
      
      const checkbox = card.querySelector('.bundle-product-card__checkbox');
      // Only add if checkbox is checked
      if (!checkbox || !checkbox.checked) return;

      const dropdown = card.querySelector('.bundle-product-card__variant-dropdown');
      if (!dropdown) return;

      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (!selectedOption || selectedOption.disabled) return;

      const variantId = parseInt(selectedOption.value, 10);
      if (!isNaN(variantId) && variantId > 0) {
        items.push({
          id: variantId,
          quantity: 1
        });
      }
    });

    return items;
  }

  /**
   * Add bundle items to cart
   * @param {string} sectionId - Section ID
   */
  async function addBundleToCart(sectionId) {
    const items = collectBundleItems(sectionId);
    
    if (items.length === 0) {
      alert('Please select at least one product to add to cart.');
      return;
    }

    const addButton = document.querySelector(`#bundle-add-all-button-${sectionId}`);
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = 'Adding...';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (!response.ok || data?.status) {
        const errorMessage = data?.description || data?.message || 'Unable to add products to cart.';
        throw new Error(errorMessage);
      }

      // Fetch updated cart and dispatch cart update event
      try {
        const cartResponse = await fetch('/cart.js', {
          headers: { Accept: 'application/json' }
        });
        const cart = await cartResponse.json();
        
        // Dispatch cart update event (same format as product cards)
        const cartUpdateEvent = new CustomEvent('cart:update', {
          detail: {
            resource: cart,
            sourceId: `bundle-section-${sectionId}`,
            data: {
              source: `bundle-section-${sectionId}`,
              itemCount: cart?.item_count ?? items.length,
              sections: undefined
            }
          }
        });
        document.dispatchEvent(cartUpdateEvent);
      } catch (error) {
        // Error fetching cart after add
      }

      // Open cart drawer
      setTimeout(() => {
        try {
          const cartDrawer = document.querySelector('cart-drawer-component');
          const cartDrawerEl = /** @type {any} */ (cartDrawer);
          if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
            cartDrawerEl.open();
          } else {
            const cartButton = document.querySelector('.header-actions__action[on\\:click="/open"]');
            if (cartButton instanceof HTMLElement && typeof cartButton.click === 'function') {
              cartButton.click();
            }
          }
        } catch (error) {
          // Error opening cart drawer
        }
      }, 250);

      // Reset button
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = `
          <span class="bundle-button-text">
            Add All <span class="bundle-product-count" id="bundle-product-count-${sectionId}">${items.length}</span> to Bag
          </span>
          <span class="bundle-bag-icon">
            <svg><!-- bag icon --></svg>
          </span>
        `;
      }
    } catch (error) {
      // Error adding bundle to cart
      alert(error.message || 'There was an issue adding products to your cart. Please try again.');
      
      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = 'Add All to Bag';
      }
    }
  }

  /**
   * Get all product IDs from similar selections sections to avoid duplicates
   * @returns {Set<number>} Set of product IDs already shown in similar selections
   */
  function getSimilarSelectionsProductIds() {
    const excludedIds = new Set();
    
    // Find all similar selections containers
    const similarSelectionsContainers = document.querySelectorAll('.similar-selections-products-container, [id^="similar-selections-products-container-"]');
    
    similarSelectionsContainers.forEach(container => {
      // Get product IDs from rendered items - check multiple possible selectors
      const items = container.querySelectorAll(
        '.SimilarSelections__Item, ' +
        '.sunice-product-card, ' +
        '.resource-list__item, ' +
        '[data-product-id]'
      );
      
      items.forEach(item => {
        // Try multiple ways to get product ID
        let productId = item.dataset?.productId || item.getAttribute('data-product-id');
        
        // If not in dataset, try to find it in nested elements
        if (!productId) {
          const productCard = item.querySelector('.sunice-product-card[data-product-id], [data-product-id]');
          if (productCard) {
            productId = productCard.dataset?.productId || productCard.getAttribute('data-product-id');
          }
        }
        
        // Also check for product ID in the card itself
        if (!productId && item.classList.contains('sunice-product-card')) {
          productId = item.getAttribute('data-product-id');
        }
        
        // Try to extract from nested product card
        if (!productId) {
          const nestedCard = item.closest('.sunice-product-card');
          if (nestedCard) {
            productId = nestedCard.dataset?.productId || nestedCard.getAttribute('data-product-id');
          }
        }
        
        if (productId) {
          const idNum = parseInt(productId, 10);
          if (!isNaN(idNum) && idNum > 0) {
            excludedIds.add(idNum);
          }
        }
      });
    });
    
    // Also check for any product cards in similar selections blocks
    const similarSelectionsBlocks = document.querySelectorAll('.sunice-similar-selections-block');
    similarSelectionsBlocks.forEach(block => {
      const productCards = block.querySelectorAll('.sunice-product-card[data-product-id]');
      productCards.forEach(card => {
        const productId = card.dataset?.productId || card.getAttribute('data-product-id');
        if (productId) {
          const idNum = parseInt(productId, 10);
          if (!isNaN(idNum) && idNum > 0) {
            excludedIds.add(idNum);
          }
        }
      });
    });
    
    return excludedIds;
  }

  /**
   * Get product IDs already in the bundle section
   * @param {string} sectionId - Section ID
   * @returns {Set<number>} Set of product IDs in the bundle
   */
  function getBundleProductIds(sectionId) {
    const bundleIds = new Set();
    const grid = document.querySelector(`#bundle-products-grid-${sectionId}`);
    
    if (grid) {
      const wrappers = grid.querySelectorAll('.bundle-product-wrapper[data-product-id]');
      wrappers.forEach(wrapper => {
        const productId = wrapper.dataset.productId;
        if (productId) {
          bundleIds.add(parseInt(productId, 10));
        }
      });
    }
    
    return bundleIds;
  }

  /**
   * Fetch product recommendations for an out-of-stock product
   * Uses JSON API first to find available products, then fetches HTML
   * @param {number} productId - Product ID to get recommendations for
   * @param {string} sectionId - Section ID
   * @returns {Promise<HTMLElement|null>} Recommended product wrapper element or null
   */
  async function fetchProductRecommendation(productId, sectionId) {
    try {
      // Get excluded product IDs (products already on the page)
      const excludedIds = getSimilarSelectionsProductIds();
      const bundleIds = getBundleProductIds(sectionId);
      const allExcludedIds = new Set([...excludedIds, ...bundleIds]);
      
      // First, try to get from stored complementary products data
      const complementaryDataScript = document.getElementById(`complementary-products-data-${sectionId}`);
      if (complementaryDataScript) {
        try {
          const storedData = JSON.parse(complementaryDataScript.textContent);
          const complementaryProducts = storedData.products?.[productId];
          
          if (complementaryProducts && complementaryProducts.length > 0) {
            
            // Find first available product that's not already on the page
            for (const recommendedProduct of complementaryProducts) {
              // Skip if already on page
              if (allExcludedIds.has(recommendedProduct.id)) continue;
              
              // Check if product has available variants
              const hasAvailableVariant = recommendedProduct.variants?.some(v => v.available === true);
              if (hasAvailableVariant) {
                // Get the original product's discount from the wrapper being replaced
                let originalDiscountPct = 0;
                const originalWrapper = document.querySelector(`.bundle-product-wrapper[data-product-id="${productId}"][data-product-available="false"]`);
                if (originalWrapper) {
                  originalDiscountPct = parseInt(originalWrapper.dataset.bundleDiscountPct || '0', 10);
                }
                
                // If replacement product has its own discount, use that; otherwise use original's discount
                const discountToUse = recommendedProduct.bundle_discount_pct || originalDiscountPct;
                recommendedProduct._bundleDiscountPct = discountToUse;
                recommendedProduct._bundleTag = recommendedProduct.bundle_tag || '';
                
                // Create and return bundle card
                const replacementCard = createBundleCardFromProduct(recommendedProduct, sectionId, productId);
                
                // Apply the discount to the replacement wrapper
                if (replacementCard) {
                  replacementCard.dataset.bundleDiscountPct = discountToUse.toString();
                }
                
                return replacementCard;
              }
            }
          }
        } catch (parseError) {
          // Error parsing stored complementary products data - will fetch from API
        }
      }
      
      // Fallback: Fetch complementary products from Search & Discovery app
      // Try complementary first, then fall back to related if no complementary products found
      let jsonUrl = `/recommendations/products.json?product_id=${productId}&limit=20&intent=complementary`;
      let jsonResponse = await fetch(jsonUrl);
      let data = null;
      
      // If complementary products don't exist or are empty, try related
      if (jsonResponse.ok) {
        const complementaryData = await jsonResponse.json();
        if (complementaryData.products && complementaryData.products.length > 0) {
          data = complementaryData;
        }
      }
      
      // Fallback to related if no complementary products
      if (!data || !data.products || data.products.length === 0) {
        jsonUrl = `/recommendations/products.json?product_id=${productId}&limit=20&intent=related`;
        jsonResponse = await fetch(jsonUrl);
        if (!jsonResponse.ok) return null;
        
        const relatedData = await jsonResponse.json();
        if (!relatedData.products || relatedData.products.length === 0) return null;
        data = relatedData;
      }
      
      // Find first available product that's not already on the page
      // Continue down the list if a product is out of stock
      for (const recommendedProduct of data.products) {
        // Skip if already on page
        if (allExcludedIds.has(recommendedProduct.id)) continue;
        
        // Check if product has available variants
        const hasAvailableVariant = recommendedProduct.variants?.some(v => v.available === true);
        if (hasAvailableVariant) {
          // Fetch full product data for images if needed
          if (!recommendedProduct.featured_image && (!recommendedProduct.images || recommendedProduct.images.length === 0)) {
            try {
              const productUrl = `/products/${recommendedProduct.handle}.json`;
              const productResponse = await fetch(productUrl);
              if (productResponse.ok) {
                const productData = await productResponse.json();
                if (productData.product) {
                  recommendedProduct.featured_image = productData.product.featured_image || recommendedProduct.featured_image;
                  recommendedProduct.images = productData.product.images || recommendedProduct.images;
                }
              }
            } catch (fetchError) {
              // Continue with available data
            }
          }
          
          // Get the original product's discount from the wrapper being replaced
          let originalDiscountPct = 0;
          const originalWrapper = document.querySelector(`.bundle-product-wrapper[data-product-id="${productId}"][data-product-available="false"]`);
          if (originalWrapper) {
            originalDiscountPct = parseInt(originalWrapper.dataset.bundleDiscountPct || '0', 10);
          }
          
          // If replacement product has its own discount, use that; otherwise use original's discount
          const discountToUse = recommendedProduct._bundleDiscountPct || recommendedProduct.bundle_discount_pct || originalDiscountPct;
          recommendedProduct._bundleDiscountPct = discountToUse;
          
          // Create and return bundle card
          const replacementCard = createBundleCardFromProduct(recommendedProduct, sectionId, productId);
          
          // Apply the discount to the replacement wrapper
          if (replacementCard) {
            replacementCard.dataset.bundleDiscountPct = discountToUse.toString();
          }
          
          return replacementCard;
        }
      }
      
      return null;
      
    } catch (error) {
      // Error fetching product recommendations
      return null;
    }
  }

  /**
   * Extract image URL from various image formats in Shopify API responses
   * @param {string|Object} image - Image data (string URL or object with src/url)
   * @returns {string} Image URL
   */
  function extractImageUrl(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    if (image.src) return image.src;
    if (image.url) return image.url;
    return '';
  }

  /**
   * Get product images (primary and secondary) from product data
   * @param {Object} productData - Product JSON data
   * @param {Object} variant - Variant data
   * @returns {{primary: string, secondary: string}} Image URLs
   */
  function getProductImages(productData, variant) {
    let primaryImage = '';
    let secondaryImage = '';
    
    // Priority 1: Variant featured image
    if (variant?.featured_image) {
      primaryImage = extractImageUrl(variant.featured_image);
    }
    
    // Priority 2: Product featured image
    if (!primaryImage && productData.featured_image) {
      primaryImage = extractImageUrl(productData.featured_image);
    }
    
    // Priority 3: Product images array
    if (!primaryImage && productData.images && productData.images.length > 0) {
      primaryImage = extractImageUrl(productData.images[0]);
    }
    
    // Get secondary image
    if (productData.images && productData.images.length > 1) {
      secondaryImage = extractImageUrl(productData.images[1]);
    }
    
    return { primary: primaryImage, secondary: secondaryImage };
  }

  /**
   * Create a bundle product card element from product JSON data
   * @param {Object} productData - Product JSON data from recommendations API
   * @param {string} sectionId - Section ID
   * @param {number} originalProductId - Original product ID being replaced
   * @returns {HTMLElement} Product wrapper element
   */
  function createBundleCardFromProduct(productData, sectionId, originalProductId) {
    const wrapper = document.createElement('div');
    wrapper.className = 'bundle-product-wrapper';
    wrapper.dataset.productId = productData.id.toString();
    wrapper.dataset.productAvailable = 'true';
    wrapper.dataset.originalProductId = originalProductId.toString();
    wrapper.dataset.isReplacement = 'true';
    
    // Find first available variant
    const availableVariant = productData.variants?.find(v => v.available) || productData.variants?.[0];
    if (!availableVariant) return null;
    
    // Get images for the available variant
    const images = getProductImages(productData, availableVariant);
    
    // Build variant options HTML
    let variantOptionsHTML = '';
    if (productData.variants && productData.variants.length > 0) {
      productData.variants.forEach(variant => {
        // Build variant label from options
        const optionValues = variant.options || [];
        const variantLabel = optionValues.join(' / ');
        
        // Get images for this variant
        const variantImages = getProductImages(productData, variant);
        const variantPrimaryImage = variantImages.primary;
        const variantSecondaryImage = variantImages.secondary;
        
        variantOptionsHTML += `
          <option 
            value="${variant.id}" 
            data-price="${variant.price}"
            data-compare-at-price="${variant.compare_at_price || 0}"
            data-image-url="${variantPrimaryImage}"
            data-secondary-image-url="${variantSecondaryImage}"
            data-variant-title="${variantLabel}"
            ${!variant.available ? 'disabled' : ''}
            ${variant.id === availableVariant.id ? 'selected' : ''}
          >
            ${variantLabel}${!variant.available ? ' - Sold Out' : ''}
          </option>
        `;
      });
    }
    
    // Calculate sale percentage
    const price = availableVariant.price || 0;
    const comparePrice = availableVariant.compare_at_price || 0;
    const isOnSale = comparePrice > price && comparePrice > 0;
    const salePercentage = isOnSale ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;
    
    // Get default variant title
    const defaultVariantTitle = availableVariant.options ? availableVariant.options.join(' / ') : '';
    
    // Create card HTML
    wrapper.innerHTML = `
      <div class="bundle-product-card" data-product-id="${productData.id}">
        <div class="product-card__image bundle-product-card__image">
          ${images.primary ? `
            <img 
              src="${images.primary}" 
              alt="${productData.title || ''}" 
              loading="lazy" 
              width="300" 
              height="300" 
              class="product-image-primary"
              data-variant-id="${availableVariant.id}"
            >
            ${images.secondary ? `
              <img 
                src="${images.secondary}" 
                alt="${productData.title || ''}" 
                loading="lazy" 
                width="300" 
                height="300" 
                class="product-image-secondary"
                data-variant-id="${availableVariant.id}"
              >
            ` : ''}
          ` : '<div class="product-placeholder">No Image</div>'}
          ${isOnSale && salePercentage > 0 ? `
            <div class="product-card sale-badge">${salePercentage}% off</div>
          ` : ''}
        </div>
        
        <div class="bundle-product-card__info">
          <div class="bundle-product-card__title">${productData.title}</div>
          <div class="bundle-product-card__brand" data-variant-title>${defaultVariantTitle}</div>
          </div>
        <div class="bundle-product-card__title-wrapper">
          <div class="bundle-product-card__variant-selector">
            <select 
              class="bundle-product-card__variant-dropdown" 
              id="variant-select-${productData.id}-${sectionId}"
              data-product-id="${productData.id}"
              aria-label="Select variant for ${productData.title}"
            >
              ${variantOptionsHTML}
            </select>
          </div>
          
          <div class="bundle-product-card__pricing">
            <label class="bundle-product-card__checkbox-wrapper">
              <input 
                type="checkbox" 
                class="bundle-product-card__checkbox" 
                id="product-checkbox-${productData.id}-${sectionId}"
                data-product-id="${productData.id}"
                checked
              >
              <span class="bundle-product-card__checkbox-checkmark"></span>
            </label>
            <div class="bundle-product-card__prices">
              <span class="bundle-product-card__price bundle-product-card__price--sale" data-price="${price}">
                ${formatMoney(price)}
              </span>
              ${isOnSale ? `
                <span class="bundle-product-card__price bundle-product-card__price--compare" data-compare-price="${comparePrice}">
                  ${formatMoney(comparePrice)}
                </span>
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
    
    return wrapper;
  }

  /**
   * Wait for similar selections to load before proceeding
   * @returns {Promise<void>}
   */
  async function waitForSimilarSelections() {
    // Wait a bit for similar selections to potentially load
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if similar selections containers exist and have products
    const containers = document.querySelectorAll('.similar-selections-products-container, [id^="similar-selections-products-container-"]');
    
    if (containers.length === 0) {
      // No similar selections, no need to wait
      return;
    }
    
    // Wait for products to load (similar selections loads asynchronously)
    let maxWait = 3000; // Max 3 seconds
    const checkInterval = 200;
    let waited = 0;
    
    while (waited < maxWait) {
      let allLoaded = true;
      for (const container of containers) {
        // Check if container has product cards
        const hasProducts = container.querySelectorAll('.SimilarSelections__Item, .sunice-product-card, .resource-list__item').length > 0;
        if (!hasProducts) {
          allLoaded = false;
          break;
        }
      }
      
      if (allLoaded) {
        // Give it a bit more time for any final rendering
        await new Promise(resolve => setTimeout(resolve, 200));
        return;
      }
      
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      waited += checkInterval;
    }
  }

  /**
   * Replace out-of-stock products with recommendations
   * Excludes products already shown in similar selections
   * @param {string} sectionId - Section ID
   */
  async function replaceOutOfStockProducts(sectionId) {
    const grid = document.querySelector(`#bundle-products-grid-${sectionId}`);
    if (!grid) return;
    
    // Find out-of-stock products
    const productWrappers = Array.from(grid.querySelectorAll('.bundle-product-wrapper[data-product-available="false"]'));
    if (productWrappers.length === 0) return;
    
    // Wait for similar selections to load
    await waitForSimilarSelections();
    
    // Replace each out-of-stock product
    for (const wrapper of productWrappers) {
      const productId = wrapper.dataset.productId;
      if (!productId || wrapper.dataset.isReplacement === 'true') continue;
      
      // CRITICAL: Get the original product's discount before replacing
      const originalDiscountPct = wrapper.dataset.bundleDiscountPct || '0';
      
      try {
        const replacement = await fetchProductRecommendation(parseInt(productId, 10), sectionId);
        if (replacement) {
          // Apply the original product's discount to the replacement
          replacement.dataset.bundleDiscountPct = originalDiscountPct;
          
          wrapper.replaceWith(replacement);
          const newCard = replacement.querySelector('.bundle-product-card');
          if (newCard) updateProductCardPrice(newCard);
          updateBundlePrice(sectionId);
        }
      } catch (error) {
        // Error replacing out of stock product
      }
    }
    
    updateBundlePrice(sectionId);
  }

  /**
   * Initialize bundle section
   * @param {string} sectionId - Section ID
   */
  function initBundleSection(sectionId) {
    const container = document.querySelector(`#boost-products-container-${sectionId}`);
    if (!container) return;

    // Replace out-of-stock products with recommendations (market-aware)
    // This runs asynchronously and won't block the UI
    // Wait for similar selections to load first to avoid duplicates
    replaceOutOfStockProducts(sectionId);
    
    // Also set up a MutationObserver to re-check if similar selections loads later
    const similarSelectionsContainers = document.querySelectorAll('.similar-selections-products-container, [id^="similar-selections-products-container-"]');
    similarSelectionsContainers.forEach(container => {
      const observer = new MutationObserver(() => {
        // Re-check and replace if needed when similar selections updates
        replaceOutOfStockProducts(sectionId);
      });
      
      observer.observe(container, {
        childList: true,
        subtree: true
      });
    });

    // Handle checkbox changes
    container.addEventListener('change', (e) => {
      if (e.target.classList.contains('bundle-product-card__checkbox')) {
        // When a checkbox changes, update ALL product cards because discount depends on all 3 being checked
        const activeGrid = container.querySelector('.bundle-products-grid:not([style*="display: none"])') || 
                          container.querySelector('.bundle-products-grid');
        if (activeGrid) {
          const allCards = activeGrid.querySelectorAll('.bundle-product-card');
          allCards.forEach((card) => {
            updateProductCardPrice(card);
          });
        }
        updateBundlePrice(sectionId);
      }
    });

    // Handle variant dropdown changes
    container.addEventListener('change', (e) => {
      if (e.target.classList.contains('bundle-product-card__variant-dropdown')) {
        const card = e.target.closest('.bundle-product-card');
        if (card) {
          updateProductCardPrice(card);
          updateBundlePrice(sectionId);
        }
      }
    });

    // Handle add all button
    const addButton = document.querySelector(`#bundle-add-all-button-${sectionId}`);
    if (addButton) {
      addButton.addEventListener('click', () => {
        addBundleToCart(sectionId);
      });
    }

    // Initial price update - delay slightly to ensure DOM is ready
    setTimeout(() => {
      updateBundlePrice(sectionId);
    }, 50);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Find all bundle sections and initialize them
      const sections = document.querySelectorAll('[id^="boost-products-section-"]');
      sections.forEach((section) => {
        const sectionId = section.id.replace('boost-products-section-', '');
        initBundleSection(sectionId);
      });
    });
  } else {
    // DOM already loaded
    const sections = document.querySelectorAll('[id^="boost-products-section-"]');
    sections.forEach((section) => {
      const sectionId = section.id.replace('boost-products-section-', '');
      initBundleSection(sectionId);
    });
  }

  // Re-initialize on dynamic content updates
  if (typeof window !== 'undefined') {
    window.initBundleSections = function() {
      const sections = document.querySelectorAll('[id^="boost-products-section-"]');
      sections.forEach((section) => {
        const sectionId = section.id.replace('boost-products-section-', '');
        initBundleSection(sectionId);
      });
    };
    
    // Expose updateBundlePrice globally for shop the look switching
    window.updateBundlePrice = updateBundlePrice;
  }
})();

